import { Observable } from 'rxjs';
import { Auth, onIdTokenChanged, User } from 'firebase/auth';
import {
  DocumentData,
  DocumentReference,
  onSnapshot,
  Query,
} from 'firebase/firestore';

/**
 * RxJS-Wrapper für das native Firebase SDK.
 *
 * `@angular/fire` brachte diese drei Helfer über `rxfire` mit. Nach dem Ausbau
 * des Pakets (kein Angular-22-Release, siehe
 * `docs/architecture/ANGULAR_22_MIGRATION_BASELINE.md`) liegen sie hier —
 * signaturgleich zu den bisherigen Aufrufstellen, damit die Konsumenten
 * unverändert bleiben.
 *
 * Bewusst schlank: nur die tatsächlich genutzten Helfer. Wer einen neuen
 * Firestore-Stream braucht, prüft zuerst, ob `toSignal()` über `onSnapshot`
 * nicht die bessere Wahl ist.
 */

export interface SnapshotIdOptions {
  /** Feldname, unter dem die Dokument-ID in das Ergebnis gemischt wird. */
  idField?: string;
}

/**
 * Live-Stream einer Firestore-Query. Entspricht `collectionData()` aus
 * `@angular/fire/firestore`.
 */
export function collectionData<T = DocumentData>(
  query: Query<T>,
  options: SnapshotIdOptions = {},
): Observable<T[]> {
  const { idField } = options;
  return new Observable<T[]>((subscriber) => {
    const unsubscribe = onSnapshot(
      query,
      (snapshot) => {
        subscriber.next(
          snapshot.docs.map((doc) => {
            const data = doc.data();
            return idField ? { ...data, [idField]: doc.id } : data;
          }),
        );
      },
      (error) => subscriber.error(error),
    );
    return { unsubscribe };
  });
}

/**
 * Live-Stream eines einzelnen Firestore-Dokuments. Entspricht `docData()` aus
 * `@angular/fire/firestore`. Existiert das Dokument nicht, liefert der Stream
 * `undefined` — genau wie die bisherige Implementierung.
 */
export function docData<T = DocumentData>(
  ref: DocumentReference<T>,
  options: SnapshotIdOptions = {},
): Observable<T> {
  const { idField } = options;
  return new Observable<T>((subscriber) => {
    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        const data = snapshot.data();
        if (data === undefined) {
          subscriber.next(undefined as T);
          return;
        }
        subscriber.next(
          idField ? ({ ...data, [idField]: snapshot.id } as T) : data,
        );
      },
      (error) => subscriber.error(error),
    );
    return { unsubscribe };
  });
}

/**
 * Stream des angemeldeten Nutzers. Entspricht `user()` aus
 * `@angular/fire/auth` — emittiert `null`, solange niemand angemeldet ist.
 *
 * Bewusst `onIdTokenChanged` und nicht `onAuthStateChanged`: rxfire trennt die
 * beiden (`user()` haengt am ID-Token, `authState()` am Anmeldestatus). Nur
 * ersteres emittiert erneut, wenn das Token erneuert wird oder sich Custom
 * Claims aendern — ein Abonnent, der laenger als bis zum ersten Wert lauscht,
 * saehe sonst stumm veraltete Claims.
 */
export function user(auth: Auth): Observable<User | null> {
  return new Observable<User | null>((subscriber) => {
    const unsubscribe = onIdTokenChanged(
      auth,
      (value) => subscriber.next(value),
      (error) => subscriber.error(error),
    );
    return { unsubscribe };
  });
}
